import Validator from "validator";
import trianglify from "trianglify";

import { createBgGradient } from "./colors";
import {
  API_ERROR,
  BMAPI_ERROR,
  ERRORS,
  HTTP,
  MANAGER_ROUTES,
  URL_CALLBACK_PARAM,
  URL_SUBSCRIBE_PARAMS,
  URL_TRACE_PARAM,
  URL_TRACE_VALUES,
} from "./constants";
import { langs } from "./dictionaries";

import { format } from "date-fns";

export function validateRoles(roleWanted, userRole) {
  return Array.isArray(roleWanted)
    ? roleWanted.includes(userRole)
    : roleWanted === userRole;
}

export function validateUUID(id) {
  return Validator.isUUID(id);
}

export function validateEmail(e) {
  return Validator.isEmail(e);
}

export function download(blob, filename = "download") {
  const url = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement("a");
  link.target = "_blank";
  link.href = url;
  link.download = filename || "download";
  document.body.appendChild(link);

  const clickHandler = () => {
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
      link.removeEventListener("click", clickHandler);
    }, 150);
  };

  link.addEventListener("click", clickHandler, false);
  link.click();
  return link;
}

export function checkTokenExpired(error) {
  return (
    error.httpCode === 401 &&
    error.code === ERRORS.TOKEN_NOT_VALID &&
    error.message.includes("ID token has expired at")
  );
}

export class BmapiError extends Error {
  constructor(message, settings, ...params) {
    super(message, ...params);

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, BmapiError);
    }

    this.name = BMAPI_ERROR;
    this.settings = settings;
  }
}

class ApiError extends Error {
  constructor(httpCode, code, message, ...params) {
    super(...params);

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }

    this.name = API_ERROR;
    this.code = code;
    this.httpCode = httpCode;
    this.message = message;
  }
}

export function handleError(response) {
  return response.json().then((data) => {
    throw new ApiError(data.HTTPCode, data.Code, data.Message);
  });
}

export function handleResponse(response) {
  if (!response.ok) return handleError(response);

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return response.json();
  } else if (contentType.includes("text/plain")) {
    return response.json();
  } else if (contentType.includes("text/csv")) {
    return response.blob();
  } else if (contentType.includes("image/png")) {
    return response.blob();
  } else if (contentType.includes("application/pdf")) {
    return response.blob();
  }
  return response.blob();
}

export function handleDownload(response) {
  return response.ok ? response.blob().then(download) : handleError(response);
}

export async function fetch(url, method = HTTP.GET, body, headers) {
  return window
    .fetch(url, {
      method,
      headers: new Headers(headers),
      ...(body ? { body } : {}),
      credentials: "include",
    })
    .then(handleResponse);
}

export function createBg(seed = null, color) {
  const pattern = trianglify({
    height: 400,
    width: 640,
    seed: seed,
    xColors: createBgGradient(color),
    yColors: "match",
    variance: 1,
  });
  return pattern.toCanvas().toDataURL();
}

export function onlyInt(e) {
  if (!/^[0-9]$/.test(e.key) && e.key !== "Enter") {
    e.preventDefault();
  }
}

export function onlyNumbers(e) {
  if (!/^[0-9,.]$/.test(e.key) && e.key !== "Enter") {
    e.preventDefault();
  }
}

export function objToQuery(obj = {}) {
  if (!obj) return "";
  const p = [];
  for (const [key, value] of Object.entries(obj)) {
    if (Array.isArray(value)) {
      for (let v of value) {
        p.push([key, v]);
      }
    } else {
      p.push([key, value]);
    }
  }
  const params = new URLSearchParams(p);
  return "?" + new URLSearchParams(params).toString();
}

export function replaceParams(url, params = {}) {
  return url.replace(
    new RegExp(
      Object.keys(params)
        .map((k) => `{${k}}`)
        .join("|") || false,
      "gi"
    ),
    (matched) => params[matched.slice(1, -1)]
  );
}

export function composeUrl(url, params, query) {
  return replaceParams(url, params) + objToQuery(query);
}

function loadConf(tenant, env) {
  try {
    const conf = require(`../configurations/${tenant}${
      env ? `.${env}` : ""
    }.json`);
    console.info(`Configuration: ${tenant}.${env || "default"}`);
    return conf;
  } catch {
    return {};
  }
}

export function createSettings(tenant, env) {
  console.log(tenant, env);
  const defaultConf = {
    ...loadConf("_default"),
    ...loadConf("_default", env),
  };
  const tenantConf = loadConf(tenant);
  const envConf = loadConf(tenant, env);
  return {
    common: {
      ...defaultConf,
      ...tenantConf,
      ...envConf,
    },
    consumer: {
      ...defaultConf,
      ...defaultConf._consumer,
      ...tenantConf,
      ...tenantConf._consumer,
      ...envConf,
      ...envConf._consumer,
    },
    manager: {
      ...defaultConf,
      ...defaultConf._manager,
      ...tenantConf,
      ...tenantConf._manager,
      ...envConf,
      ...envConf._manager,
    },
  };
}

function loadTheme(theme) {
  try {
    const conf = require(`../configurations/themes/${theme}.json`);
    console.info(`Theme: ${theme}`);
    return conf;
  } catch {
    return {};
  }
}

export function createThemeConf(theme, app) {
  const defaultTheme = loadTheme("_default");
  const customTheme = loadTheme(theme);
  return {
    ...defaultTheme,
    ...customTheme,
    ...(customTheme[`_${app}`] ? customTheme[`_${app}`] : {}),
  };
}

export function getData(key, empty = {}) {
  return JSON.parse(localStorage.getItem(key)) || empty;
}

export function setData(key, data, empty) {
  const newData = {
    ...getData(key, empty),
    ...data,
  };
  localStorage.setItem(key, JSON.stringify(newData));
  return newData;
}

export function parseTokenId(token_id) {
  return JSON.parse(
    window.atob(token_id.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
  );
}

export function uuidv4() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    var r = (Math.random() * 16) | 0,
      v = c == "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export default {
  checkTokenExpired,
  composeUrl,
  createBg,
  createBgGradient,
  createSettings,
  createThemeConf,
  download,
  fetch,
  getData,
  handleDownload,
  handleError,
  handleResponse,
  objToQuery,
  onlyNumbers,
  parseTokenId,
  setData,
  uuidv4,
  validateEmail,
  validateRoles,
  validateUUID,
};

export function mergeSubscrPlans(tiers, items) {
  const arr = [];
  if (!items || !tiers || !items.length || !tiers.length) {
    return arr;
  }
  var itemsMap = new Map(items.map((item) => [item.bme_plan_key, item]));
  tiers.map((tier) => {
    if (tier.action) {
      arr.push(tier);
    } else {
      const item = itemsMap.get(tier.id);
      if (item) {
        item.specs = tier.specs;
        item.best = tier.best;
        arr.push(item);
      }
    }
  });
  return arr;
}

/*
export function getRouteBase(location) {
  //const path = window.location.pathname.split("/");
  return (
    //location.protocol + "//" + location.host + "/" + path[1] + "/" + path[2]
    //location.protocol + "//" + location.host + "/" + path[1]
    location.protocol + "//" + location.host
  );
}
*/
export function getRouteBase() {
  const l = window.location;
  console.log("$$$$$$$$$$$$$$$", l);
  return l.protocol + "//" + l.host;
}

export function getEmptyArray(n) {
  const arr = [];
  for (let i = 0; i < n; ++i) {
    arr.push("");
  }
  return arr;
}

export function getEmptyMap(n) {
  let map = new Map();
  for (let i = 0; i < n.length; i++) {
    map.set(n[i], "");
  }
  return map;
}

export function getLanguageLabel(code) {
  for (let i = 0; i < langs.length; ++i) {
    if (langs[i].value === code) {
      return langs[i];
    }
  }
  return {};
}

export function getFields(arrobjects, field) {
  const arr = [];
  for (let i = 0; i < arrobjects.length; ++i) {
    arr.push(arrobjects[i][field]);
  }
  return arr;
}

export function getMapFields(arrobjects, field, lang) {
  let map = new Map();
  if (Array.isArray(arrobjects)) {
    for (let i = 0; i < arrobjects.length; ++i) {
      map.set(arrobjects[i][lang], arrobjects[i][field]);
    }
  }
  return map;
}

export function compareTranslations(o1, o2) {
  return o1.lang < o2.lang ? -1 : 1;
}

export function compareByTitle(o1, o2) {
  return o1.title.toLowerCase() < o2.title.toLowerCase() ? -1 : 1;
}

export function parseBmarkenDate(param) {
  if (!param) {
    return null;
  }
  if (param.startsWith("0001-01-01T")) {
    return null;
  }
  var timestamp = Date.parse(param);

  if (isNaN(timestamp) == false) {
    return new Date(timestamp);
  }

  return null;
}

export function hourToString(ISOstr) {
  return ISOstr.slice(11, 16).replace(/:/g, "");
}

/*
export function priceText(price) {
  return Number.parseFloat(price / 100).toFixed(2);
}*/

export function priceText(price) {
  const n = Number(price);
  if (!Number.isFinite(n)) return "-";
  return (n / 100).toFixed(2);
}

/*
export function decodeArray(
  arr,
  mapping,
  fieldFrom = "value",
  fieldTo = "label"
) {
  if (!arr || !mapping) {
    return [];
  }
  const newMap = new Map(
    mapping.map((mappedElement) => [
      mappedElement[fieldFrom],
      mappedElement[fieldTo],
    ])
  );
  return arr.map((item) => newMap.get(item));
}

export const isInObjArray = (arr, value, fieldName = "value") => {
  if (!arr) {
    return false;
  }
  const filtered = arr.filter((obj) => obj[fieldName] === value);
  return filtered && filtered.length;
};
*/
export const isInArray = (arr, value) => {
  if (!arr) {
    return false;
  }
  return arr.indexOf(value) !== -1;
};

export const decodeFromArray = (
  arr,
  value,
  fieldFrom = "value",
  fieldTo = "label"
) => {
  if (!arr || !Array.isArray(arr)) {
    return "";
  }
  const filtered = arr.filter((obj) => obj[fieldFrom] === value);
  return filtered && filtered.length ? filtered[0][fieldTo] : "";
};

export const checkNullArray = (arr) => {
  return !arr || arr === null ? [] : arr;
};

export const intlFormatMessage = (message, dflt, intl) =>
  message ? intl.formatMessage(message) : dflt;

export function getQueryVariable(query, variable) {
  if (query.startsWith("?")) {
    query = query.substring(1);
  }
  const vars = query.split("&");
  for (var i = 0; i < vars.length; i++) {
    var pair = vars[i].split("=");
    if (decodeURIComponent(pair[0]) == variable) {
      return decodeURIComponent(pair[1]);
    }
  }
}
/*
export function addParam(param, newval, search) {
  var regex = new RegExp("([?;&])" + param + "[^&;]*[;&]?");
  var query = search.replace(regex, "$1").replace(/&$/, "");

  return (
    (query.length > 2 ? query + "&" : "?") +
    (newval ? param + "=" + newval : "")
  );
}*/

export function propagateUrlTracing() {
  const params = [
    URL_TRACE_PARAM,
    URL_CALLBACK_PARAM,
    URL_SUBSCRIBE_PARAMS.SUBSCRIPTION,
    URL_SUBSCRIBE_PARAMS.PAYMENT,
  ];
  const query = params.reduce((prevVal, currVal) => {
    const val = getQueryVariable(location.search, currVal);
    if (!val) return prevVal;
    return prevVal ? `${prevVal}&${currVal}=${val}` : `${currVal}=${val}`;
  }, "");
  /*
  const trace = getQueryVariable(location.search, URL_TRACE_PARAM);
  const callback = getQueryVariable(location.search, URL_CALLBACK_PARAM);
  const subscriptionId = getQueryVariable(
    location.search,
    URL_SUBSCRIBE_PARAMS.SUBSCRIPTION
  );
  const paymentId = getQueryVariable(
    location.search,
    URL_SUBSCRIBE_PARAMS.PAYMENT
  );
  return addUrlParameters(trace, callback);
  */
  return query ? "?" + query : "";
}

/*
export function addUrlParameters(trace, callback) {
  let search = "";
  if (trace) {
    search += "&" + URL_TRACE_PARAM + "=" + trace;
  }
  if (callback) {
    search += "&" + URL_CALLBACK_PARAM + "=" + encodeURIComponent(callback);
  }
  if (search) {
    search = "?" + search.substring(1);
  }
  return search;
}*/
/*
export const propagateCallback = (url) => {
  const name = "callback";
  const callback = getQueryVariable(location.search, name);

  if (callback) {
    const arr = url.split("?");
    const sep = arr && arr.length > 1 ? "&" : "?";
    url = arr[0] + sep + name + "=" + callback;
  }

  return url;
};
*/
/*
export const checkExternalUrl = (url, subscriptionSlug, language) => {
  let newUrl = url?.replace(
    "$$ALTERNATIVESITE$$",
    DOMAIN_CONFIG.ALTERNATIVESITE
  );
  if (subscriptionSlug) {
    newUrl = newUrl?.replace("$$SUBSCRIPTION_SLUG$$", subscriptionSlug);
  }
  if (language) {
    newUrl = newUrl?.replace("$$LANGUAGE$$", language);
  }
  return newUrl;
};
*/
/*
export const isJoinSubscribeBuyUrl = (callback) => {
  return !!callback?.startsWith(MANAGER_ROUTES.SUBSCRIBE_STEP2);
};*/
export const getCallbackUrl = () => {
  const trace = getQueryVariable(location.search, URL_TRACE_PARAM);
  const callback = getQueryVariable(location.search, URL_CALLBACK_PARAM);
  /*
  if (trace === URL_TRACE_VALUES.SUBSCRIBE) {
    if (isJoinSubscribeBuyUrl(callback)) {
      return callback;
    }
  }*/
  if (trace === URL_TRACE_VALUES.EXTERNAL) {
    const decoded = decodeURIComponent(callback);
    return decoded;
  }
  return null;
};

export const getSignInSubscriptionData = () => {
  const trace = getQueryVariable(location.search, URL_TRACE_PARAM);
  const paymentId = getQueryVariable(
    location.search,
    URL_SUBSCRIBE_PARAMS.PAYMENT
  );
  const subscriptionId = getQueryVariable(
    location.search,
    URL_SUBSCRIBE_PARAMS.SUBSCRIPTION
  );
  return {
    isSubscribing: trace === URL_TRACE_VALUES.SUBSCRIBE && !!paymentId,
    paymentId,
    subscriptionId,
  };
};

export const getSignInSubscribeUrl = ({ subscriptionId, paymentId }) => {
  return `${MANAGER_ROUTES.SIGN_IN}?${URL_TRACE_PARAM}=${URL_TRACE_VALUES.SUBSCRIBE}&${URL_SUBSCRIBE_PARAMS.SUBSCRIPTION}=${subscriptionId}&${URL_SUBSCRIBE_PARAMS.PAYMENT}=${paymentId}`;
};

export const getSubscribeStep2Url = ({
  baseUrl,
  subscriptionId,
  paymentId,
}) => {
  const path = getSubscribeStep2Path({ subscriptionId, paymentId });
  return `${baseUrl}${path}`;
};

export const getSubscribeStep2Path = ({ subscriptionId, paymentId }) => {
  return `${MANAGER_ROUTES.SUBSCRIBE_STEP2}?payment=${paymentId}&subscription=${subscriptionId}`;
};

export const isSubscriptionCompleted = (subscription) => {
  if (!subscription) {
    return true;
  }
  return !!subscription.friendly_url;
};

export const isAlphanumeric = (text) => {
  return text.match(/^([0-9]|[a-z])+([0-9a-z]+)$/i);
};

export function checkFiscalCodeIT(cf) {
  if (!/^[0-9A-Z]{16}$/.test(cf)) return false;
  var s = 0;
  var even_map = "BAFHJNPRTVCESULDGIMOQKWZYX";
  for (var i = 0; i < 15; i++) {
    var c = cf[i];
    var n = 0;
    if ("0" <= c && c <= "9") n = c.charCodeAt(0) - "0".charCodeAt(0);
    else n = c.charCodeAt(0) - "A".charCodeAt(0);
    if ((i & 1) === 0) n = even_map.charCodeAt(n) - "A".charCodeAt(0);
    s += n;
  }
  if ((s % 26) + "A".charCodeAt(0) !== cf.charCodeAt(15)) return false;
  return true;
}

export function checkVatNumberIT(pi) {
  pi = pi.replace(/\s/g, "");
  if (!/^[0-9]{11}$/.test(pi)) return false;
  var s = 0;
  for (var i = 0; i < 11; i++) {
    var n = pi.charCodeAt(i) - "0".charCodeAt(0);
    if ((i & 1) === 1) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    s += n;
  }
  if (s % 10 !== 0) return false;
  return true;
}

export function getSubscriptionLogo(sub) {
  console.log("------------------------", sub);
  let logoUrl = "",
    logoId = "";
  const mediaContentsCount = sub?.media_contents?.length;
  if (mediaContentsCount) {
    const mediaContent = sub.media_contents[0];
    logoUrl = mediaContent?.url;
    logoId = mediaContent?.id;
  }
  return { logoId, logoUrl };
}

export function isSubscriptionIncomplete(sub) {
  if (!sub) {
    return false;
  }
  return !sub.friendly_url || !getSubscriptionLogo(sub).logoUrl;
}

export function convertToSlug(Text) {
  return Text.toLowerCase()
    .replace(/[^\w ]+/g, "")
    .replace(/ +/g, "-");
}

export function oneYearExpiration() {
  var now = new Date();
  now.setFullYear(now.getFullYear() + 1);
  return now;
}

export function hexToRgb(hex) {
  var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? "rgba(" +
        parseInt(result[1], 16) +
        "," +
        parseInt(result[2], 16) +
        "," +
        parseInt(result[3], 16) +
        "," +
        1 +
        ")"
    : null;
}

export function removeParams(params) {
  for (const property in params) {
    if (params[property] === null) {
      delete params[property];
    }
  }
  return params;
}

export function getArrLabel(arr, val) {
  const label = arr.filter((obj) => obj.value === val);
  return label[0]?.label;
}

export function getParameter(key) {
  return new URLSearchParams(location.search).get(key);
}

export function getLabelFromArrObj(code, arr, prop, label) {
  const filtered = arr.filter((obj) => obj[prop] === code);
  return filtered && filtered.length ? filtered[0][label] : "";
}

export function compareObjects(obj1, obj2) {
  return obj1.id === obj2.id;
}

export function arrayUniqueValues(array) {
  var a = array.concat();
  for (var i = 0; i < a.length; ++i) {
    for (var j = i + 1; j < a.length; ++j) {
      if (a[i] === a[j]) a.splice(j--, 1);
    }
  }

  return a;
}

export const fixDateString = (date) => {
  date = date.replace(/-/g, "/");
  var newData = date.replace(/(\d+[/])(\d+[/])/, "$2$1");
  return new Date(newData);
};

export function isInt(value) {
  if (typeof value !== "number") {
    return false;
  }

  return value === Math.floor(value);
}

export function parseBmeIntToInt(n) {
  if (!n) {
    return n;
  }

  if (isInt(n)) {
    let ni = parseInt(n / 100);
    return ni;
  }

  return n;
}

export function parseBmeIntToFloat(n) {
  if (!n) {
    return n;
  }

  if (isInt(n)) {
    let nf = (n / 100.0).toFixed(2);
    return nf;
  }

  return n;
}

export function sortByDaysOfTheWeek(a, b) {
  const daysOfWeekOrder = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

  // objects with [day] field
  return daysOfWeekOrder.indexOf(a.day) - daysOfWeekOrder.indexOf(b.day);
}

export function compareByOrder(a, b) {
  return a?.order - b?.order;
}

export function refreshPage() {
  window.location.reload();
}

/**
 * Sanitizes a string so it can be safely used as a filename, then optionally
 * appends a sanitized extension.
 *
 * @param {string} name - The original (unsanitized) file name.
 * @param {string} [ext] - The optional file extension (without the leading dot).
 * @returns {string}
 */
export function sanitizeForFileName(name, ext = "") {
  // Define the "control characters + invalid filename chars" range as a string.
  // Note that we must double-escape backslashes inside the string.
  const CONTROL_CHAR_RANGE = "\\u0000-\\u001F"; // as a string (escaped)
  const INVALID_CHARS_PATTERN = `[<>:"/\\\\|?*${CONTROL_CHAR_RANGE}]`;

  // Create the regex from the pattern string to avoid direct control chars in the literal:
  const INVALID_CHARS_REGEX = new RegExp(INVALID_CHARS_PATTERN, "g");

  // Remove invalid characters from the filename part
  let sanitizedName = name.replace(INVALID_CHARS_REGEX, "");

  // Remove trailing dots or spaces (important for Windows)
  sanitizedName = sanitizedName.replace(/[.\s]+$/, "");

  // If it becomes empty, default to "untitled"
  if (!sanitizedName) {
    sanitizedName = "untitled";
  }

  // Now sanitize the extension if provided
  let sanitizedExt = ext
    .replace(INVALID_CHARS_REGEX, "") // remove invalid chars
    .replace(/[.\s]+$/, "") // remove trailing dots/spaces
    .replace(/^\.+/, ""); // remove leading dot(s)

  // If there's a sanitized extension, append it with a leading dot
  if (sanitizedExt) {
    return `${sanitizedName}.${sanitizedExt}`;
  }

  // Otherwise, just return the sanitized filename
  return sanitizedName;
}

export function fromBmarkenDateToDateTime(param) {
  if (!param) {
    return null;
  }
  if (param.startsWith("0001-01-01T")) {
    return null;
  }
  var timestamp = Date.parse(param);

  if (isNaN(timestamp) == false) {
    return format(new Date(timestamp), "dd/MM/yyyy HH:mm");
  }

  return null;
}

export function fromBmarkenDateToDay(param) {
  if (!param) {
    return null;
  }
  if (param.startsWith("0001-01-01T")) {
    return null;
  }
  var timestamp = Date.parse(param);

  if (isNaN(timestamp) == false) {
    return format(new Date(timestamp), "dd/MM/yyyy");
  }

  return null;
}
